/**
 * AnalysisLedger — the analysis page's task-level audit ledger.
 *
 * Console-style: a meta row (rows × columns, current sort), a
 * horizontally scrollable table, every column sortable, and optional
 * detail columns (section / output type / skills / steps) that appear
 * only when the benchmark's export carries that data — an empty column
 * is noise, not information. Rows navigate to the task page.
 */

import { useState } from 'react'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { DIFFICULTY_LABELS, DIFFICULTY_TONES, taskScoreFor } from '@/components/evals/analysis'
import { formatScore } from '@/components/evals/format'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type {
  AnalysisTask,
  AnalysisTaskDifficulty,
  BenchmarkTaskFairness,
  BenchmarkTaskTrainingReadiness,
  BenchmarkTaskVerifierStatus,
} from '@/api/generated/model'

/** Optional per-task detail columns from the benchmark export. */
export interface LedgerTaskDetails {
  section: string
  world: string
  outputType: string
  requiredSkills: string[]
  steps: number
  fairness: BenchmarkTaskFairness
  modelFailure: string
  modelFailureDetail: string
  failedStep: string
  verifierStatus: BenchmarkTaskVerifierStatus
  verifierIssue: string
  verifierIssueDetail: string
  trainingReadiness: BenchmarkTaskTrainingReadiness
  goldResponse: string
  goldResponseType: string
}

interface AnalysisLedgerProps {
  tasks: AnalysisTask[]
  /** Selected model filter; null shows headline scores. */
  model: string | null
  /** Where a row navigates on click — caller owns the route family. */
  taskPath: (taskSlug: string) => string
  /** Extra columns keyed by task slug; omit for surfaces without them. */
  details?: Map<string, LedgerTaskDetails>
  /** Best single-rollout score selector; adds a sortable Best column. */
  bestFor?: (taskSlug: string, model: string | null) => number | null
  /** Zero-task copy override. The default reads as "export hasn't landed",
   * which is a lie when the caller pre-filtered `tasks` to nothing. */
  empty?: { title: string; description: string }
  /** Rendered beside the search box, e.g. an active-filter banner. */
  toolbar?: React.ReactNode
}

type SortKey =
  | 'task'
  | 'id'
  | 'section'
  | 'score'
  | 'best'
  | 'difficulty'
  | 'runs'
  | 'models'
  | 'steps'
  | 'fairness'
  | 'model_failure'
  | 'failed_step'
  | 'verifier_status'
  | 'verifier_issue'
  | 'training'
  | 'gold_type'

interface LedgerRow {
  task: AnalysisTask
  score: number | null
  best: number | null
  details: LedgerTaskDetails | undefined
}

const DIFFICULTY_ORDER: Record<AnalysisTaskDifficulty, number> = {
  easy: 0,
  medium: 1,
  hard: 2,
  unscored: 3,
}

/**
 * Triage pill tones — literal-color exception like DIFFICULTY_TONES
 * (semantic tokens offer no success/warning tint). Severity ranks drive
 * sorting: default descending puts the worst rows on top.
 */
type PillTone = 'success' | 'warning' | 'danger' | 'neutral'

const PILL_COLORS: Record<Exclude<PillTone, 'neutral'>, string> = {
  success: 'hsl(126 45% 34%)',
  warning: 'hsl(35 65% 38%)',
  danger: 'hsl(0 55% 42%)',
}

const FAIRNESS_META: Record<BenchmarkTaskFairness, { tone: PillTone; rank: number }> = {
  high: { tone: 'success', rank: 0 },
  mid: { tone: 'warning', rank: 1 },
  low: { tone: 'danger', rank: 2 },
  '': { tone: 'neutral', rank: -1 },
}

const VERIFIER_META: Record<BenchmarkTaskVerifierStatus, { tone: PillTone; rank: number }> = {
  clean: { tone: 'success', rank: 0 },
  unknown: { tone: 'neutral', rank: 1 },
  needs_review: { tone: 'warning', rank: 2 },
  grading_issue: { tone: 'danger', rank: 3 },
  suspected_broken: { tone: 'danger', rank: 3 },
}

const TRAINING_META: Record<BenchmarkTaskTrainingReadiness, { tone: PillTone; rank: number }> = {
  train_now: { tone: 'success', rank: 0 },
  eval_only: { tone: 'neutral', rank: 1 },
  distill_only: { tone: 'neutral', rank: 2 },
  needs_process_verifier: { tone: 'warning', rank: 3 },
  fix_first: { tone: 'danger', rank: 4 },
  drop: { tone: 'danger', rank: 5 },
}

function TriagePill({ value, tone }: { value: string; tone: PillTone }): React.JSX.Element {
  if (value === '') return <span className="text-xs text-muted-foreground">—</span>
  return (
    <span
      className="inline-flex whitespace-nowrap rounded-md border border-border px-1.5 py-0.5 text-xs"
      style={
        tone === 'neutral'
          ? undefined
          : { color: PILL_COLORS[tone], borderColor: PILL_COLORS[tone] }
      }
    >
      {value.split('_').join(' ')}
    </span>
  )
}

/** Label with a clamped secondary detail line (console two-line cell). */
function LabelDetailCell({ label, detail }: { label: string; detail: string }): React.JSX.Element {
  if (label === '' && detail === '') {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  return (
    <div className="max-w-80">
      <p className="text-sm text-foreground">{label.split('_').join(' ') || '—'}</p>
      {detail !== '' && (
        <p className="line-clamp-2 text-xs leading-4 text-muted-foreground">{detail}</p>
      )}
    </div>
  )
}

function compareRows(a: LedgerRow, b: LedgerRow, key: SortKey): number {
  switch (key) {
    case 'task':
      return a.task.display_name.localeCompare(b.task.display_name)
    case 'id':
      return a.task.slug.localeCompare(b.task.slug)
    case 'section':
      return (a.details?.section ?? '').localeCompare(b.details?.section ?? '')
    case 'score': {
      // Unscored rows sink to the bottom in both directions.
      if (a.score === null || b.score === null) {
        return (a.score === null ? 1 : 0) - (b.score === null ? 1 : 0)
      }
      return b.score - a.score
    }
    case 'best': {
      if (a.best === null || b.best === null) {
        return (a.best === null ? 1 : 0) - (b.best === null ? 1 : 0)
      }
      return b.best - a.best
    }
    case 'difficulty':
      return DIFFICULTY_ORDER[a.task.difficulty] - DIFFICULTY_ORDER[b.task.difficulty]
    case 'runs':
      return b.task.run_count - a.task.run_count
    case 'models':
      return b.task.model_scores.length - a.task.model_scores.length
    case 'steps':
      return (b.details?.steps ?? 0) - (a.details?.steps ?? 0)
    case 'fairness':
      return (
        FAIRNESS_META[b.details?.fairness ?? ''].rank -
        FAIRNESS_META[a.details?.fairness ?? ''].rank
      )
    case 'model_failure':
      return (b.details?.modelFailure ?? '').localeCompare(a.details?.modelFailure ?? '')
    case 'failed_step':
      return (b.details?.failedStep ?? '').localeCompare(a.details?.failedStep ?? '')
    case 'verifier_status':
      return (
        VERIFIER_META[b.details?.verifierStatus ?? 'unknown'].rank -
        VERIFIER_META[a.details?.verifierStatus ?? 'unknown'].rank
      )
    case 'verifier_issue':
      return (b.details?.verifierIssue ?? '').localeCompare(a.details?.verifierIssue ?? '')
    case 'training':
      return (
        TRAINING_META[b.details?.trainingReadiness ?? 'eval_only'].rank -
        TRAINING_META[a.details?.trainingReadiness ?? 'eval_only'].rank
      )
    case 'gold_type':
      return (b.details?.goldResponseType ?? '').localeCompare(a.details?.goldResponseType ?? '')
  }
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left',
}: {
  label: string
  sortKey: SortKey
  sort: { key: SortKey; ascending: boolean }
  onSort: (key: SortKey) => void
  align?: 'left' | 'right'
}): React.JSX.Element {
  const active = sort.key === sortKey
  return (
    <TableHead className={align === 'right' ? 'text-right' : undefined}>
      <button
        type="button"
        onClick={() => {
          onSort(sortKey)
        }}
        aria-label={`Sort by ${label}${active ? `, currently ${sort.ascending ? 'ascending' : 'descending'}` : ''}`}
        className={`inline-flex items-center gap-1 font-medium hover:text-foreground ${active ? 'text-foreground' : ''}`}
      >
        {label}
        {active &&
          (sort.ascending ? (
            <ArrowUp aria-hidden="true" className="size-3" />
          ) : (
            <ArrowDown aria-hidden="true" className="size-3" />
          ))}
      </button>
    </TableHead>
  )
}

function SkillChips({ skills }: { skills: string[] }): React.JSX.Element {
  const visible = skills.slice(0, 3)
  return (
    <span className="flex flex-wrap gap-1">
      {visible.map((skill) => (
        <span key={skill} className="rounded-md bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
          {skill}
        </span>
      ))}
      {skills.length > visible.length && (
        <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
          +{skills.length - visible.length}
        </span>
      )}
    </span>
  )
}

function ModelScoresCell({ task }: { task: AnalysisTask }): React.JSX.Element {
  if (task.model_scores.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-default text-sm tabular-nums text-foreground">
          {task.model_scores.length}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-0.5">
          {task.model_scores.map((entry) => (
            <p key={entry.model_slug} className="font-mono text-xs">
              {entry.model_slug}: {formatScore(entry.score)}
            </p>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

export function AnalysisLedger({
  tasks,
  model,
  taskPath,
  details,
  bestFor,
  empty,
  toolbar,
}: AnalysisLedgerProps): React.JSX.Element {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; ascending: boolean }>({
    key: 'score',
    ascending: false,
  })

  const toggleSort = (key: SortKey): void => {
    setSort((previous) =>
      previous.key === key ? { key, ascending: !previous.ascending } : { key, ascending: false }
    )
  }

  const hasSection = details !== undefined && Array.from(details.values()).some((d) => d.section !== '')
  const hasOutputType =
    details !== undefined && Array.from(details.values()).some((d) => d.outputType !== '')
  const hasSkills =
    details !== undefined && Array.from(details.values()).some((d) => d.requiredSkills.length > 0)
  const hasSteps = details !== undefined && Array.from(details.values()).some((d) => d.steps > 0)
  const detailValues = details !== undefined ? Array.from(details.values()) : []
  const hasFairness = detailValues.some((d) => d.fairness !== '')
  const hasModelFailure = detailValues.some((d) => d.modelFailure !== '')
  const hasFailedStep = detailValues.some((d) => d.failedStep !== '')
  const hasVerifierStatus = detailValues.some((d) => d.verifierStatus !== 'unknown')
  const hasVerifierIssue = detailValues.some((d) => d.verifierIssue !== '')
  const hasTraining = detailValues.some((d) => d.trainingReadiness !== 'eval_only')
  const hasGoldType = detailValues.some((d) => d.goldResponseType !== '')

  const needle = query.trim().toLowerCase()
  const rows: LedgerRow[] = tasks
    .map((task) => ({
      task,
      score: taskScoreFor(task, model),
      best: bestFor !== undefined ? bestFor(task.slug, model) : null,
      details: details?.get(task.slug),
    }))
    .filter(
      ({ task, details: rowDetails }) =>
        needle === '' ||
        task.display_name.toLowerCase().includes(needle) ||
        task.slug.toLowerCase().includes(needle) ||
        (rowDetails !== undefined &&
          (rowDetails.requiredSkills.some((skill) => skill.toLowerCase().includes(needle)) ||
            rowDetails.modelFailure.toLowerCase().includes(needle) ||
            rowDetails.failedStep.toLowerCase().includes(needle) ||
            rowDetails.verifierIssue.toLowerCase().includes(needle) ||
            rowDetails.trainingReadiness.includes(needle)))
    )
    .sort((a, b) => {
      const base = compareRows(a, b, sort.key)
      return sort.ascending ? -base : base
    })

  if (tasks.length === 0) {
    return (
      <EmptyState
        title={empty?.title ?? 'No tasks in this eval yet'}
        description={empty?.description ?? 'Tasks appear once the export lands in the bucket.'}
      />
    )
  }

  const columnCount =
    5 +
    (bestFor !== undefined ? 1 : 0) +
    (hasSection ? 1 : 0) +
    (hasOutputType ? 1 : 0) +
    (hasSkills ? 1 : 0) +
    (hasSteps ? 1 : 0) +
    (hasFairness ? 1 : 0) +
    (hasModelFailure ? 1 : 0) +
    (hasFailedStep ? 1 : 0) +
    (hasVerifierStatus ? 1 : 0) +
    (hasVerifierIssue ? 1 : 0) +
    (hasTraining ? 1 : 0) +
    (hasGoldType ? 1 : 0)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
          }}
          placeholder="Search task, id, skill…"
          aria-label="Search tasks"
          className="max-w-xs"
        />
        {toolbar}
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No tasks match" description="Try a different search." />
      ) : (
        <div className="rounded-md border border-border">
          <div className="flex items-center justify-between border-b border-border px-4 py-2 text-xs text-muted-foreground">
            <span>
              {rows.length} rows × {columnCount} columns
            </span>
            <span>Task-level ledger</span>
          </div>
          <div className="overflow-x-auto">
            <Table className="min-w-max">
              <TableHeader>
                <TableRow>
                  <SortHeader label="ID" sortKey="id" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Task" sortKey="task" sort={sort} onSort={toggleSort} />
                  {hasSection && (
                    <SortHeader label="Section" sortKey="section" sort={sort} onSort={toggleSort} />
                  )}
                  {hasOutputType && <TableHead>Output type</TableHead>}
                  {hasSkills && <TableHead>Required skills</TableHead>}
                  {hasSteps && (
                    <SortHeader label="Steps" sortKey="steps" sort={sort} onSort={toggleSort} align="right" />
                  )}
                  <SortHeader label="Score" sortKey="score" sort={sort} onSort={toggleSort} align="right" />
                  {bestFor !== undefined && (
                    <SortHeader label="Best" sortKey="best" sort={sort} onSort={toggleSort} align="right" />
                  )}
                  <SortHeader label="Difficulty" sortKey="difficulty" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Runs" sortKey="runs" sort={sort} onSort={toggleSort} align="right" />
                  <SortHeader label="Models" sortKey="models" sort={sort} onSort={toggleSort} align="right" />
                  {hasFairness && (
                    <SortHeader label="Fairness" sortKey="fairness" sort={sort} onSort={toggleSort} />
                  )}
                  {hasModelFailure && (
                    <SortHeader label="Model failure" sortKey="model_failure" sort={sort} onSort={toggleSort} />
                  )}
                  {hasFailedStep && (
                    <SortHeader label="Failed step" sortKey="failed_step" sort={sort} onSort={toggleSort} />
                  )}
                  {hasVerifierStatus && (
                    <SortHeader label="Verifier status" sortKey="verifier_status" sort={sort} onSort={toggleSort} />
                  )}
                  {hasVerifierIssue && (
                    <SortHeader label="Verifier issue" sortKey="verifier_issue" sort={sort} onSort={toggleSort} />
                  )}
                  {hasTraining && (
                    <SortHeader label="Training" sortKey="training" sort={sort} onSort={toggleSort} />
                  )}
                  {hasGoldType && (
                    <SortHeader label="Gold type" sortKey="gold_type" sort={sort} onSort={toggleSort} />
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(({ task, score, best, details: rowDetails }) => (
                  <TableRow
                    key={task.id}
                    // Keyboard path to every task — the chart dots are
                    // deliberately non-focusable (no focus ring), so the
                    // ledger row is the accessible navigation surface.
                    tabIndex={0}
                    aria-label={`Open task ${task.display_name}`}
                    className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => {
                      navigate(taskPath(task.slug))
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        navigate(taskPath(task.slug))
                      }
                    }}
                  >
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {task.slug}
                    </TableCell>
                    <TableCell className="max-w-96 text-sm text-foreground">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block cursor-default truncate">{task.display_name}</span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-96">
                          <p className="whitespace-pre-wrap text-xs">{task.display_name}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    {hasSection && (
                      <TableCell className="text-sm text-foreground">
                        {rowDetails?.section ?? ''}
                      </TableCell>
                    )}
                    {hasOutputType && (
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {rowDetails?.outputType ?? ''}
                      </TableCell>
                    )}
                    {hasSkills && (
                      <TableCell className="max-w-72">
                        {rowDetails !== undefined && rowDetails.requiredSkills.length > 0 ? (
                          <SkillChips skills={rowDetails.requiredSkills} />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    )}
                    {hasSteps && (
                      <TableCell className="text-right text-sm tabular-nums">
                        {rowDetails !== undefined && rowDetails.steps > 0 ? rowDetails.steps : '—'}
                      </TableCell>
                    )}
                    <TableCell className="text-right font-mono text-sm tabular-nums">
                      {formatScore(score)}
                    </TableCell>
                    {bestFor !== undefined && (
                      <TableCell className="text-right font-mono text-sm tabular-nums">
                        {formatScore(best)}
                      </TableCell>
                    )}
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span
                          aria-hidden="true"
                          className="size-2 rounded-full"
                          style={{ backgroundColor: DIFFICULTY_TONES[task.difficulty] }}
                        />
                        {DIFFICULTY_LABELS[task.difficulty]}
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {task.run_count}
                    </TableCell>
                    <TableCell className="text-right">
                      <ModelScoresCell task={task} />
                    </TableCell>
                    {hasFairness && (
                      <TableCell>
                        <TriagePill
                          value={rowDetails?.fairness ?? ''}
                          tone={FAIRNESS_META[rowDetails?.fairness ?? ''].tone}
                        />
                      </TableCell>
                    )}
                    {hasModelFailure && (
                      <TableCell>
                        <LabelDetailCell
                          label={rowDetails?.modelFailure ?? ''}
                          detail={rowDetails?.modelFailureDetail ?? ''}
                        />
                      </TableCell>
                    )}
                    {hasFailedStep && (
                      <TableCell className="max-w-64 text-sm text-foreground">
                        {rowDetails !== undefined && rowDetails.failedStep !== '' ? (
                          // Step ids are snake_case, so they carry no space for
                          // CSS to break on and paint over the next column
                          // without an explicit truncate.
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="block cursor-default truncate">
                                {rowDetails.failedStep}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-96">
                              <p className="whitespace-pre-wrap text-xs">{rowDetails.failedStep}</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    )}
                    {hasVerifierStatus && (
                      <TableCell>
                        <TriagePill
                          value={rowDetails?.verifierStatus ?? ''}
                          tone={VERIFIER_META[rowDetails?.verifierStatus ?? 'unknown'].tone}
                        />
                      </TableCell>
                    )}
                    {hasVerifierIssue && (
                      <TableCell>
                        <LabelDetailCell
                          label={rowDetails?.verifierIssue ?? ''}
                          detail={rowDetails?.verifierIssueDetail ?? ''}
                        />
                      </TableCell>
                    )}
                    {hasTraining && (
                      <TableCell>
                        <TriagePill
                          value={rowDetails?.trainingReadiness ?? ''}
                          tone={TRAINING_META[rowDetails?.trainingReadiness ?? 'eval_only'].tone}
                        />
                      </TableCell>
                    )}
                    {hasGoldType && (
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {rowDetails?.goldResponseType ?? ''}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  )
}
